/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ProductWarehouseInventoryInformation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { getInventoryLabel } from '../../mil-search-result-container/components';
import { IImageSettings } from '@msdyn365-commerce/core-internal';
import MilProductComponent from '../../../components/MilProductComponent';
import { ICustomerWarehouseEntity_V3, IUnitOfMeasureConversionResponseMapEntity } from '../../../actions/DataServiceEntities.g';
import { IMillProductsListPageResources } from '../mill-products-list-page.props.autogenerated';
import { displayedInventoryMethod } from '../../mil-quick-order/mil-quick-order.props.autogenerated';
import { getEstimatedAvailability } from '../../../utils/InventoryDisplayHelper';

export interface IMilSearchResultsItems {
    products?: SimpleProduct[] | undefined;
    context: any;
    telemetryContent?: ITelemetryContent;
    id: string;
    typeName: string;
    imageSettings?: IImageSettings;
    uomData: IUnitOfMeasureConversionResponseMapEntity[] | undefined;
    resources: IMillProductsListPageResources;
    allowBackNavigation?: boolean;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    showListView: boolean;
}

interface ICustInv {
    inventory: number;
}

export const MilSearchResultsItems: React.FC<IMilSearchResultsItems> = props => {
    const {
        products = [],
        context,
        telemetryContent,
        id,
        typeName,
        imageSettings,
        uomData,
        resources,
        quickviewSlot,
        productComparisonButton,
        showListView
    } = props;

    const inventoryMethod = context.app.config.displayedInventoryMethod;

    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const [estimatedAvailabilites, setEstimatedAvailabilites] = React.useState<ProductWarehouseInventoryInformation | undefined>();
    const [warehouses, setWarehouses] = React.useState<ICustomerWarehouseEntity_V3[] | undefined>();

    React.useEffect(() => {
        const itemIds = Array.from(new Set(products.map((product: SimpleProduct) => product.RecordId)));

        void getEstimatedAvailability(context, itemIds).then(({ estimatedAvailabilities, warehouses }) => {
            setEstimatedAvailabilites(estimatedAvailabilities);
            setWarehouses(warehouses);
        });
    }, [products]);

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <ul className={showListView ? 'mil-list-view' : 'mil-grid-view'}>
            {products.map((product, idx) => {
                const key = product?.RecordId ?? idx;
                const productUom = uomData?.find(uom => uom.ItemRecId === product.RecordId);
                const productBehavior = product.Behavior;
                const availInvs = estimatedAvailabilites?.ProductWarehouseInventoryAvailabilities?.filter(
                    inv => inv.ProductId === product.RecordId
                );
                const newCustInv: ICustInv[] = [];
                if (warehouses && availInvs) {
                    warehouses.map(wh => {
                        const inventoryInfo = availInvs.find(inv => inv.InventLocationId === wh.AvailableWarehouse);
                        if (inventoryInfo) {
                            const newWhEntry = {
                                inventory:
                                    inventoryMethod === displayedInventoryMethod.physicalAvailable
                                        ? inventoryInfo.PhysicalAvailable || 0
                                        : inventoryInfo.TotalAvailable || 0
                            };
                            newCustInv.push(newWhEntry);
                        }
                    });
                }

                const aggProductInvAvail = estimatedAvailabilites?.AggregatedProductInventoryAvailabilities?.find(
                    inv => inv.ProductId === product.RecordId
                );
                const totalInv =
                    warehouses && warehouses.length > 0 && newCustInv
                        ? newCustInv?.reduce((acc, curr) => acc + (curr.inventory >= 0 ? curr.inventory : 0), 0)
                        : aggProductInvAvail && inventoryMethod === displayedInventoryMethod.physicalAvailable
                        ? aggProductInvAvail?.PhysicalAvailableQuantity
                        : aggProductInvAvail?.TotalAvailableQuantity;

                return (
                    <li className='ms-product-search-result__item' key={key}>
                        <MilProductComponent
                            context={context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFree}
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            shouldDisplayPrice={!!products}
                            ratingAriaLabel={resources.ratingAriaLabel}
                            allowBack={true}
                            id={id}
                            key={product.RecordId}
                            typeName={typeName}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            productComparisonButton={productComparisonButton}
                            inventoryLabel={getInventoryLabel(3453451345, product)}
                            isPriceMinMaxEnabled={true}
                            priceResources={priceResources}
                            swatchItemAriaLabel={resources.swatchItemAriaLabel}
                            desiredNonstockCommerceName={context.app.config.nonstockCommerceState}
                            nonstockCommerceState={context.app.config.desiredNonstockCommerceName}
                            stockStatusText={resources.stockStatusText}
                            productUom={productUom}
                            minimumOrderQtyLabelText={resources.minimumOrderQtyLabelText}
                            productBehavior={productBehavior}
                            availInvs={totalInv || 0}
                            availableStockLabel={resources.availableStockLabel}
                            isEnabledProductDescription={showListView} //isEnabledProductDescription replaces showListView
                        />
                    </li>
                );
            })}
        </ul>
    );
};

export default MilSearchResultsItems;
